import { motion } from 'framer-motion';
import { Eye, Bell, TrendingUp, ArrowRight } from 'lucide-react';

const WorkshopBenefits = () => {
  const benefits = [
    {
      icon: Eye,
      title: 'See what Amazon sees',
      description: 'Access the same 50+ behavioral signals that Amazon\'s systems use to evaluate your account health.',
      color: 'purple'
    },
    {
      icon: Bell,
      title: 'Get early warnings',
      description: 'Know when your metrics are trending toward enforcement — while you still have time to fix them.',
      color: 'blue'
    },
    {
      icon: TrendingUp,
      title: 'Track rate of change',
      description: 'See whether each metric is improving or deteriorating, not just where it stands today.',
      color: 'emerald'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      purple: { bg: 'bg-purple-50', border: 'border-purple-100', icon: 'text-purple-500' },
      blue: { bg: 'bg-blue-50', border: 'border-blue-100', icon: 'text-blue-500' },
      emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', icon: 'text-emerald-500' }
    };
    return colors[color];
  };

  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="workshop-benefits-section">
      <div className="max-w-5xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-purple-600">
            What You'll Learn
          </p>
          <h2 className="font-display text-slate-800 text-3xl sm:text-4xl lg:text-5xl mt-3">
            In This Free Workshop
          </h2>
        </motion.div>

        {/* Benefits Grid */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => {
            const colorClasses = getColorClasses(benefit.color);
            return (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl border border-slate-200 p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-purple-200"
              >
                <span className={`w-14 h-14 rounded-xl ${colorClasses.bg} ${colorClasses.border} border flex items-center justify-center`}>
                  <benefit.icon className={`w-7 h-7 ${colorClasses.icon}`} strokeWidth={1.75} />
                </span>
                <h3 className="font-display text-slate-800 text-xl mt-6">
                  {benefit.title}
                </h3>
                <p className="text-slate-500 leading-relaxed mt-3">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <a
            href="https://amazonworkshop.section3.ai/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 text-lg font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-full transition-all hover:scale-105 shadow-lg shadow-purple-500/30"
            data-testid="benefits-cta"
          >
            Save My Seat — It's Free
            <ArrowRight className="w-5 h-5" />
          </a>
          <p className="mt-4 text-sm text-slate-500">
            60 minutes · Live workshop · Limited seats
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WorkshopBenefits;
